import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'
import { generateUniqueId } from 'src/shared/utils'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'

export type InsertChat = Database['public']['Tables']['chat']['Insert']
export type Chat_ = Database['public']['Tables']['chat']['Row']

const isAdminTaggedMessage = (content: unknown) => {
  const raw =
    typeof content === 'string' ? content : JSON.stringify(content ?? '')
  try {
    return JSON.parse(raw || '{}')?.admin === true
  } catch {
    return false
  }
}

export const useChatByQuoteId = (quote_id: number | undefined | null) => {
  const supabase = useSupabaseClient()
  const isAdmin = useIsAdmin()

  const { data: chat, isPending, isLoading } = useQuery({
    queryKey: [
      queryKeys.chats,
      'byQuote',
      quote_id,
      isAdmin ? 'admin' : 'customer',
    ],
    queryFn: async () => {
      if (!quote_id) return null

      const { data, error } = await supabase
        .from('chat')
        .select('*, message(*)')
        .eq('quote_id', quote_id)
        .maybeSingle()

      if (error) throw error
      if (!data || isAdmin) return data

      const messages = (data.message ?? []).filter(
        (message) => !isAdminTaggedMessage(message.content)
      )
      return { ...data, message: messages }
    },
    enabled: !!quote_id,
  })

  return { chat, isPending, isLoading }
}

export const useChatApi = ({
  org_id,
  chat_id,
  fetchAll = false,
  page,
  pageSize = 20,
}: {
  org_id?: string
  chat_id?: number
  fetchAll?: boolean
  page?: number
  pageSize?: number
}) => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const isPaginated = fetchAll && page !== undefined

  const {
    data: chats,
    isLoading,
    isPending,
  } = useQuery({
    queryKey: [
      queryKeys.chats,
      org_id,
      chat_id,
      fetchAll,
      ...(isPaginated ? [page, pageSize] : []),
    ].filter((v) => v !== false),
    queryFn: async () => {
      let query = supabase
        .from('chat')
        .select(
          '*, message(*), organization!chat_org_id_fkey(id, name), quote!chat_quote_id_fkey(id, public_id)',
          {
            ...(isPaginated ? { count: 'exact' } : {}),
          }
        )
        .order('created_at', { ascending: false })

      if (org_id) {
        query = query.eq('org_id', org_id)
      }

      if (chat_id) {
        query = query.eq('id', chat_id)
      }

      if (isPaginated && page !== undefined) {
        const from = page * pageSize
        const to = from + pageSize - 1
        query = query.range(from, to)
      }

      return await query
    },
    enabled: !!org_id || !!chat_id || fetchAll,
  })

  const { data: buyerIntentChats } = useQuery({
    queryKey: [queryKeys.chats, 'buyerIntent'],
    queryFn: async () => {
      const { data } = await supabase
        .from('chat')
        .select('id, name, org_id, organization!chat_org_id_fkey(id, name)')
        .eq('status', 'buyer_intent_detected')
      return data ?? []
    },
    enabled: fetchAll,
  })

  const createChat = useMutation({
    mutationFn: async (insertChat: InsertChat) => {
      let res
      if (!insertChat.quote_id) {
        res = await supabase
          .from('quote')
          .insert({
            org_id: insertChat.org_id,
            name: 'New order',
            public_id: generateUniqueId(Math.floor(Math.random() * 10000) + 1),
            source: 'chat',
          })
          .select('id')
          .single()
      }

      return await supabase
        .from('chat')
        .insert({
          org_id: insertChat.org_id,
          name: 'New chat',
          quote_id: insertChat.quote_id ?? res.data?.id,
        })
        .select('id')
        .single()
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.chats] })
      return data
    },
  })

  const updateChat = useMutation({
    mutationFn: async (insertChat: InsertChat) =>
      await supabase.from('chat').update(insertChat).eq('id', insertChat.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.chats] })
    },
  })

  const deleteChat = useMutation({
    mutationFn: async (id: number) => {
      // Delete quotes with no quote items first
      const quote_id = await supabase
        .from('chat')
        .select('quote_id')
        .eq('id', id)
        .single()

      if (quote_id.data?.quote_id) {
        const quote_items = await supabase
          .from('quote_item')
          .select('id')
          .eq('quote_id', quote_id.data?.quote_id)

        if (quote_items.data?.length === 0) {
          await supabase
            .from('quote')
            .delete()
            .eq('id', quote_id.data?.quote_id)
        }
      }

      // Then delete the chat from database
      return await supabase.from('chat').delete().eq('id', id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.chats] })
    },
  })

  return {
    chats,
    isLoading,
    isPending,
    buyerIntentChats: buyerIntentChats ?? [],
    updateChat,
    deleteChat,
    createChat,
  }
}
